import React from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { Download } from "lucide-react";
import { ProductionEventLogEntry } from "../hooks/useProductionEventLog";
import OSCard from "./OSCard";

interface Props {
  title: string;
  events: ProductionEventLogEntry[];
  loading?: boolean;
  error?: string | null;
  emptyText?: string;
  onExportExcel?: () => void;
}

const TH: React.FC<{ w: number; children: React.ReactNode }> = ({ w, children }) => (
  <th
    className="border border-gray-300 bg-gray-200 text-gray-700 font-semibold text-center text-[11px] h-[42px] px-2 py-1.5 leading-tight align-middle"
    style={{ width: w, minWidth: w }}
  >
    {children}
  </th>
);

const TD: React.FC<{
  align?: "left" | "center";
  title?: string;
  children: React.ReactNode;
}> = ({ align = "left", title, children }) => (
  <td
    className={`border border-gray-300 h-[26px] px-1.5 py-0.5 text-[11px] text-gray-700 ${
      align === "center" ? "text-center" : "text-left"
    }`}
    title={title}
  >
    {children}
  </td>
);

const fmt = (value?: string): string => {
  if (!value) return "";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("DD.MM.YYYY HH:mm") : "";
};

const docRef = (ref?: { typeCode?: string; number?: string }): string => {
  if (!ref) return "";
  const parts = [ref.typeCode, ref.number].filter(Boolean);
  return parts.join(" ");
};

const ProductionEventLogTable: React.FC<Props> = ({
  title,
  events,
  loading,
  error,
  emptyText,
  onExportExcel,
}) => {
  const { t } = useTranslation();
  const colCount = 10;

  return (
    <OSCard
      title={title}
      actions={onExportExcel && (
        <button
          onClick={onExportExcel}
          className="flex items-center gap-1 px-2 py-1 bg-white/20 hover:bg-white/30 text-white rounded text-[11px] transition-colors"
          title={t("productionEventLog.exportExcel")}
        >
          <Download size={12} />
          {t("productionEventLog.exportExcel")}
        </button>
      )}
    >
      {error && (
        <div className="px-3 py-2 text-[11px] text-red-500">{error}</div>
      )}
      <div className="overflow-x-auto bg-white">
        <table className="border-collapse w-full min-w-[900px]">
          <thead>
            <tr>
              <TH w={40}>{t("os.statusLog.rowNo")}</TH>
              <TH w={130}>{t("productionEventLog.cols.subprocess")}</TH>
              <TH w={110}>{t("productionEventLog.cols.machine")}</TH>
              <TH w={140}>{t("productionEventLog.cols.status")}</TH>
              <TH w={120}>{t("productionEventLog.cols.performedBy")}</TH>
              <TH w={120}>{t("productionEventLog.cols.verifiedBy")}</TH>
              <TH w={115}>{t("productionEventLog.cols.occurredAt")}</TH>
              <TH w={115}>{t("productionEventLog.cols.recordedAt")}</TH>
              <TH w={44}>{t("productionEventLog.cols.correction")}</TH>
              <TH w={120}>{t("productionEventLog.cols.document")}</TH>
            </tr>
          </thead>
          <tbody>
            {events.length === 0 ? (
              <tr>
                <td
                  colSpan={colCount}
                  className="border border-gray-300 h-[32px] px-2 text-center text-[11px] text-gray-500"
                >
                  {loading ? "..." : (emptyText ?? t("productionEventLog.empty"))}
                </td>
              </tr>
            ) : (
              events.map((ev, idx) => (
                <tr
                  key={ev.eventId || idx}
                  className={`hover:bg-blue-50 focus-within:bg-blue-50 ${
                    ev.supersededByEventId ? "opacity-50 line-through" : ""
                  }`}
                >
                  <TD align="center">{idx + 1}</TD>
                  <TD title={ev.subprocessName}>{ev.subprocessName ?? ""}</TD>
                  <TD title={ev.machineName}>{ev.machineName ?? ""}</TD>
                  <TD title={ev.statusName}>
                    {ev.statusName ?? ev.statusCode}
                  </TD>
                  <TD title={ev.performedByName}>{ev.performedByName ?? ""}</TD>
                  <TD title={ev.verifiedByName}>{ev.verifiedByName ?? ""}</TD>
                  <TD align="center">{fmt(ev.occurredAt)}</TD>
                  <TD align="center">{fmt(ev.recordedAt)}</TD>
                  <TD align="center">{ev.isCorrection ? "✓" : ""}</TD>
                  <TD title={docRef(ev.documentRef)}>{docRef(ev.documentRef)}</TD>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </OSCard>
  );
};

export default ProductionEventLogTable;
